import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useOrg } from '@/stores/org'
import { motionSafeScrollBehavior } from '@/lib/motion'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { EmptyState, type EmptyStateIllustrationKind } from '@/components/ui/empty-state'
import { ListError } from '@/components/ui/list-row'

/** Layout listens for this to expand its create-org form when membership is empty. */
export const ORG_PICKER_EVENT = 'togence:open-org-picker'

/**
 * The one picker handoff: notify Layout's listener, bring the sticky header
 * into view, and fall back to focusing + opening the header org select when no
 * listener handled the event (only meaningful when there is a real choice).
 */
export function openOrgPicker(): void {
  window.dispatchEvent(new CustomEvent(ORG_PICKER_EVENT))
  window.scrollTo({ top: 0, behavior: motionSafeScrollBehavior() })
  if (useOrg.getState().orgs.length === 0) return
  const sel = document.querySelector('header select')
  if (sel instanceof HTMLSelectElement) {
    sel.focus()
    try {
      sel.showPicker()
    } catch {
      // picker unavailable — focus still applied
    }
  }
}

export type OrgGuardState = 'ready' | 'loading' | 'error' | 'missing'

/** Per-page copy only — the guard owns predicate, states, and the action. */
export type OrgGuardFlavor = {
  /** i18n key for the no-org empty-state title */
  titleKey: string
  /** optional i18n key for the description line */
  descKey?: string
  illustration?: EmptyStateIllustrationKind
  /**
   * Inset for the placeholder — the guard owns it so every console page that
   * renders `node` sits the same distance from the shell edge. Defaults to
   * `m-4`; pass a value only to genuinely override it.
   */
  className?: string
  /** optional secondary link target (in-console) — paired with secondaryLabelKey */
  secondaryTo?: string
  /** i18n key for the secondary link label — paired with secondaryTo */
  secondaryLabelKey?: string
}

export type OrgGuardResult = {
  state: OrgGuardState
  /** rendered guard body — null when the org context is usable */
  node: ReactNode
}

/**
 * "Org context not ready" for org-scoped surfaces: render `node` in place of
 * the page body until an org context is usable. Distinct blocked states:
 * `error` (orgs fetch failed with an empty cache — retry via refresh),
 * `loading` (orgs still loading), `missing` (no active org — picker handoff).
 */
export function useOrgGuard({ titleKey, descKey, illustration, className = 'm-4', secondaryTo, secondaryLabelKey }: OrgGuardFlavor): OrgGuardResult {
  const { t } = useTranslation()
  const { activeOrgId, orgs, loading: orgsLoading, error: orgsError, refresh: refreshOrgs } = useOrg()

  // Org list fetch failed with nothing usable cached — retry before any
  // org-scoped content; a stale-but-usable orgs list is not blocked.
  if (!activeOrgId || (orgsError && orgs.length === 0)) {
    if (orgsError) {
      return { state: 'error', node: <ListError message={orgsError} onRetry={refreshOrgs} className={className} /> }
    }
    return {
      state: orgsLoading ? 'loading' : 'missing',
      node: (
        <EmptyState
          illustration={illustration}
          title={t(titleKey)}
          description={descKey ? t(descKey) : undefined}
          className={className}
          secondaryAction={
            secondaryTo && secondaryLabelKey ? (
              <Link
                to={secondaryTo}
                className="inline-flex min-h-9 items-center rounded-full border border-enterprise-border bg-enterprise-surface px-4 text-xs tabular-nums font-medium text-enterprise-text hover:bg-enterprise-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-enterprise-indigo"
              >
                {t(secondaryLabelKey)}
              </Link>
            ) : undefined
          }
          action={
            orgsLoading ? (
              <Skeleton className="h-9 w-40" />
            ) : (
              <Button
                type="button"
                variant="outline"
                className="min-h-9 rounded-full px-4 text-xs font-medium"
                onClick={openOrgPicker}
              >
                {t('common.selectOrg')}
              </Button>
            )
          }
        />
      ),
    }
  }
  return { state: 'ready', node: null }
}
